import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import EnglishData from "utils/EnglishData.json";
import {
  Continent,
  CONTINENT_LABELS,
  FILTERABLE_CONTINENTS,
  countryMatchesInput,
  resolveContinent,
} from "utils/functions";
import { AvailableLanguages, Country } from "utils/types";

interface WorldMapProps {
  data: Country[];
  language?: AvailableLanguages;
  focusedCountry?: Country | string;
  /**
   * Crops the map to a single continent. A whole world map is unreadable on a
   * phone, but one continent fills the same box at a usable scale — this is
   * how the mobile layout keeps a real map instead of falling back to a list.
   */
  focusedContinent?: Continent;
  /**
   * Builds the link for a country. The default goes to its page; a route
   * passes one that carries the route along, so clicking a country on the
   * route's own map keeps the thread instead of dropping it — the same way
   * clicking one of the stops does.
   */
  countryTo?: (displayName: string) => string;
  /** Hide the built-in filter dropdown and zoom buttons (mobile provides its own). */
  showControls?: boolean;
  /** Use the full height of the container instead of reserving a strip for floating chrome. */
  fillHeight?: boolean;
  /**
   * When set, clicking a country calls this instead of navigating to its
   * page — used by the quiz's "find it on the map" questions.
   */
  onCountrySelect?: (country: Country) => void;
  /** Countries to paint as correct/incorrect after a map answer is given. */
  highlights?: Partial<Record<"correct" | "incorrect", string>>;
  /**
   * Per-country fill colors, keyed by English name — the mechanism behind
   * the Learn "lenses" (thematic maps coloured by religion, language,
   * government type...). Countries absent from the map keep the base fill.
   */
  countryColors?: Record<string, string>;
  /**
   * The floating "Do you want to explore X?" line that names whatever the
   * pointer is over. On by default for the map you browse; the embedded maps
   * (Learn routes and lenses) turn it off because there the map illustrates a
   * story rather than inviting you to wander off mid-read. Answer surfaces
   * force it off regardless — see `onCountrySelect`.
   */
  showHoverInvite?: boolean;
}

// The map artwork's own coordinate system.
const VIEW_BOX_WIDTH = 1009;
const VIEW_BOX_HEIGHT = 652;
const DEFAULT_VIEW_BOX = `0 0 ${VIEW_BOX_WIDTH} ${VIEW_BOX_HEIGHT}`;

// Smallest region a continent crop may zoom to, in artwork units — roughly a
// fifth of the map, which still reads as a recognisable part of the world.
const MIN_CROP_WIDTH = 200;
const MIN_CROP_HEIGHT = 130;

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;
// Minimum pointer travel (px) before a press-and-move counts as a pan drag
// instead of a click, so dragging the map doesn't also navigate a country.
const DRAG_THRESHOLD = 6;

const copy = {
  [AvailableLanguages.EN]: {
    mapLabel: "Interactive world map",
    filterLegend: "Filter by continent",
    continentToggle: (count: number) =>
      count > 0 ? `Continents (${count})` : "Continents",
    clearFilters: "Clear",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    zoomReset: "Reset zoom",
    noMatches: "No countries match the selected continents.",
    maskedCountry: "Unidentified country",
  },
  [AvailableLanguages.ES]: {
    mapLabel: "Mapa interactivo del mundo",
    filterLegend: "Filtrar por continente",
    continentToggle: (count: number) =>
      count > 0 ? `Continentes (${count})` : "Continentes",
    clearFilters: "Limpiar",
    zoomIn: "Acercar",
    zoomOut: "Alejar",
    zoomReset: "Restablecer zoom",
    noMatches: "Ningún país coincide con los continentes seleccionados.",
    maskedCountry: "País sin identificar",
  },
};

const MapContainer = styled.div`
  /* Absolutely positioned (instead of a normal flow block) so this box is
     always pinned to its nearest positioned ancestor's exact bounds, no
     matter what sits before it in the DOM (search bar, bubbles, etc.).
     Letting it sit in normal flow while also claiming height:100% made it
     spill past the viewport and forced page-level scrollbars in both axes
     once zoom/filters added more content. */
  position: absolute;
  inset: 0;
  overflow: hidden;

  & > p {
    letter-spacing: 0.5px;
    font-family: "Gaegu", cursive;
    color: #1b264f;
    text-shadow: 2px 5px 3px #ebf8e1;
    font-size: 2.5em;
    position: absolute;
    z-index: 2;
    top: 0;
    left: 10px;
    pointer-events: none;
    margin: 0;
    min-height: 1.2em;
  }
`;

// Only the map itself scrolls/pans; overlay controls (filters, zoom buttons,
// the invite text) live outside this element so dragging the map can never
// carry them off-screen.
const MapViewport = styled.div<{ $pannable: boolean; $dragging: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: ${({ $pannable }) => ($pannable ? "auto" : "hidden")};
  touch-action: ${({ $pannable }) => ($pannable ? "none" : "auto")};
  user-select: ${({ $pannable }) => ($pannable ? "none" : "auto")};
  cursor: ${({ $pannable, $dragging }) =>
    $pannable ? ($dragging ? "grabbing" : "grab") : "default"};
`;

const CustomSVG = styled.svg<{ $zoom: number; $fill?: boolean }>`
  position: absolute;
  width: 100%;
  /* Deliberately shorter than the container (instead of ~95%). The map's
     internal aspect ratio (1009x652) gets fit and letterboxed differently
     depending on the window's own aspect ratio, so any given country can
     land anywhere within the SVG's box on a wide-enough range of screens —
     there's no fixed pixel offset that's safe for floating chrome to sit in
     *within* that box. Shrinking the box itself reserves a strip below it
     that the map can never draw into, regardless of aspect ratio, for the
     zoom controls and the home-screen nav buttons to live in without ever
     covering a real country (this is what fixed the "New Zealand is stuck
     under the Learn/Self-quiz buttons" report). */
  /* $fill skips that reserved strip: the compact/mobile map has no floating
     buttons sitting on top of it, so it can use the whole box. */
  height: ${({ $fill }) => ($fill ? "100%" : "86%")};
  top: ${({ $fill }) => ($fill ? "0" : "3%")};
  transform: scale(${({ $zoom }) => $zoom});
  /* Scaling from the top-left corner (instead of the center) means the
     extra, zoomed-in content only grows down and to the right — i.e. into
     the scroll range overflow:auto can actually reach. A center origin
     pushed half the zoomed map above/left of the scrollable (0,0) origin,
     where no amount of scrolling or dragging could ever reveal it. */
  transform-origin: 0 0;
  transition: transform 160ms ease;
`;

const Path = styled.path<{
  $choseLanguage: boolean;
}>`
  stroke-width: 1;
  cursor: ${({ $choseLanguage }) => ($choseLanguage ? "pointer" : "default")};
  &:hover {
    fill: ${({ $choseLanguage }) => $choseLanguage && "lightblue"};
  }
`;

const MapLink = styled(Link)<{ $dimmed: boolean }>`
  opacity: ${({ $dimmed }) => ($dimmed ? 0.12 : 1)};
  transition: opacity 160ms ease;
  ${({ $dimmed }) => $dimmed && "pointer-events: none;"}
`;

const ControlsColumn = styled.div`
  position: absolute;
  z-index: 3;
  left: 16px;
  bottom: 14px;
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const ControlButton = styled.button`
  cursor: pointer;
  border: 1px solid rgba(245, 226, 200, 0.35);
  color: #f5e2c8;
  background: rgba(27, 38, 79, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  width: 38px;
  height: 38px;
  border-radius: 12px;
  font-family: "Finger Paint", cursive;
  font-size: 1.1rem;
  line-height: 1;
  transition:
    transform 120ms ease,
    background 120ms ease,
    border 120ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    background: rgba(27, 38, 79, 0.7);
    border-color: rgba(245, 226, 200, 0.6);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const FilterPanel = styled.div`
  position: absolute;
  z-index: 3;
  /* OceanScene renders its own name search box (SearchForm) in the same
     top-right corner, sized min(520px, 80vw) and flush with the right edge.
     Sitting to its left (instead of stacking below/behind it) keeps both
     controls reachable without overlapping. */
  top: 16px;
  right: calc(min(520px, 80vw) + 24px);
`;

const ContinentDropdown = styled.div`
  position: relative;
  flex: 0 0 auto;
`;

const DropdownToggle = styled.button<{ $active: boolean; $open: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 6px 12px;
  font-family: "Finger Paint", cursive;
  font-size: 0.78rem;
  letter-spacing: 0.2px;
  white-space: nowrap;
  border: 1px solid
    ${({ $active, $open }) =>
      $active || $open
        ? "rgba(173, 216, 230, 0.85)"
        : "rgba(245, 226, 200, 0.35)"};
  background: ${({ $active }) =>
    $active ? "rgba(173, 216, 230, 0.4)" : "rgba(27, 38, 79, 0.5)"};
  color: #f5e2c8;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition:
    background 120ms ease,
    border 120ms ease;

  &:hover {
    border-color: rgba(173, 216, 230, 0.85);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-block;
  transition: transform 120ms ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
  font-size: 0.7em;
`;

const DropdownPanel = styled.div`
  position: absolute;
  z-index: 4;
  top: calc(100% + 6px);
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 190px;
  max-height: min(320px, 60vh);
  overflow: auto;
  padding: 8px;
  border-radius: 14px;
  border: 1px solid rgba(245, 226, 200, 0.25);
  background: rgba(20, 33, 61, 0.94);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.4);
`;

const DropdownOption = styled.button<{ $active: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  border-radius: 10px;
  padding: 7px 9px;
  font-family: "Gaegu", cursive;
  font-size: 1rem;
  border: 1px solid transparent;
  background: ${({ $active }) =>
    $active ? "rgba(173, 216, 230, 0.22)" : "transparent"};
  color: #f5e2c8;
  transition: background 120ms ease;

  &:hover {
    background: rgba(173, 216, 230, 0.16);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const OptionCheck = styled.span<{ $active: boolean }>`
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 0.7rem;
  line-height: 1;
  border: 1px solid rgba(245, 226, 200, 0.5);
  background: ${({ $active }) =>
    $active ? "rgba(173, 216, 230, 0.85)" : "transparent"};
  color: #182848;
`;

const DropdownFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px solid rgba(245, 226, 200, 0.18);
`;

const ClearButton = styled.button`
  cursor: pointer;
  border-radius: 999px;
  padding: 5px 11px;
  border: 1px solid rgba(245, 226, 200, 0.35);
  background: rgba(27, 38, 79, 0.5);
  color: #f5e2c8;
  font-family: "Finger Paint", cursive;
  font-size: 0.78rem;
  white-space: nowrap;

  &:hover {
    border-color: rgba(173, 216, 230, 0.85);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const inviteToExplore = (country: Country, lang?: AvailableLanguages) =>
  lang === AvailableLanguages.ES
    ? `Quieres explorar ${country["spanish-name"]}?`
    : `Do you want to explore ${country["english-name"]}?`;

export const WorldMap = ({
  data,
  language,
  focusedCountry,
  focusedContinent,
  countryTo,
  showControls = true,
  fillHeight = false,
  onCountrySelect,
  highlights,
  countryColors,
  showHoverInvite = true,
}: WorldMapProps) => {
  const [hoveredCountry, setHoveredCountry] = useState<Country | undefined>(
    undefined
  );
  const [viewBox, setViewBox] = useState(DEFAULT_VIEW_BOX);
  const svgRef = useRef<SVGSVGElement>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [selectedContinents, setSelectedContinents] = useState<Set<Continent>>(
    new Set()
  );
  const [isContinentMenuOpen, setIsContinentMenuOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const continentDropdownRef = useRef<HTMLDivElement>(null);
  const mapViewportRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
    moved: boolean;
  } | null>(null);
  const wasDraggedRef = useRef(false);

  const isSpanish = language === AvailableLanguages.ES;
  const uiLanguage = language ?? AvailableLanguages.EN;
  const t = copy[uiLanguage];
  const continentLabels = CONTINENT_LABELS[uiLanguage];

  useEffect(() => {
    if (!isContinentMenuOpen) return;

    const handlePointerDown = (e: MouseEvent) => {
      if (!continentDropdownRef.current?.contains(e.target as Node)) {
        setIsContinentMenuOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsContinentMenuOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isContinentMenuOpen]);

  const focusedCountryValue =
    typeof focusedCountry === "string" ? focusedCountry : "";

  const focusedInviteToExploreLine = useMemo(() => {
    if (!language) return undefined;
    if (!focusedCountryValue) return undefined;

    const match = data.find((country) =>
      countryMatchesInput(country, focusedCountryValue)
    );

    if (!match) return undefined;
    return inviteToExplore(match, language);
  }, [data, focusedCountryValue, language]);

  const continentByCountry = useMemo(() => {
    const map = new Map<string, Continent | undefined>();
    data.forEach((country) => {
      const englishName = country["english-name"];
      const rawContinent = (EnglishData as Record<string, { Continent?: string }>)[
        englishName
      ]?.Continent;
      map.set(englishName, resolveContinent(englishName, rawContinent));
    });
    return map;
  }, [data]);

  // Crop the artwork to the requested continent by unioning the rendered
  // bounding boxes of its countries. Reading them off the DOM (rather than
  // parsing the path data) keeps a single source of truth for the geometry.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    if (!focusedContinent) {
      setViewBox(DEFAULT_VIEW_BOX);
      return;
    }

    const nodes = svg.querySelectorAll<SVGGraphicsElement>(
      `[data-continent="${focusedContinent}"]`
    );

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    nodes.forEach((node) => {
      const box = node.getBBox();
      if (box.width === 0 && box.height === 0) return;
      minX = Math.min(minX, box.x);
      minY = Math.min(minY, box.y);
      maxX = Math.max(maxX, box.x + box.width);
      maxY = Math.max(maxY, box.y + box.height);
    });

    if (!Number.isFinite(minX)) {
      setViewBox(DEFAULT_VIEW_BOX);
      return;
    }

    const padX = (maxX - minX) * 0.06 + 4;
    const padY = (maxY - minY) * 0.06 + 4;

    let x = minX - padX;
    let y = minY - padY;
    let width = maxX - minX + padX * 2;
    let height = maxY - minY + padY * 2;

    // Floor the crop. A continent whose only member is a speck — Antarctica
    // resolves to the French Southern Territories, a 13-unit dot on a
    // 1009x652 map — otherwise produced a ~75x zoom: no recognisable
    // coastline, just hugely magnified fragments of whatever else happened to
    // be nearby. Keeping a minimum span preserves enough context to tell
    // where on Earth you are.
    const grow = (start: number, span: number, min: number, limit: number) => {
      if (span >= min) return [start, span] as const;
      const grown = Math.min(min, limit);
      const centered = start + span / 2 - grown / 2;
      // Stay inside the artwork so the crop never shows empty canvas.
      return [Math.min(Math.max(centered, 0), limit - grown), grown] as const;
    };

    [x, width] = grow(x, width, MIN_CROP_WIDTH, VIEW_BOX_WIDTH);
    [y, height] = grow(y, height, MIN_CROP_HEIGHT, VIEW_BOX_HEIGHT);

    setViewBox(`${x} ${y} ${width} ${height}`);
  }, [focusedContinent, data]);

  const isCountryVisible = (country: Country) => {
    if (selectedContinents.size === 0) return true;
    const continent = continentByCountry.get(country["english-name"]);
    return !!continent && selectedContinents.has(continent);
  };

  const hasActiveFilters = selectedContinents.size > 0;
  const visibleCount = hasActiveFilters
    ? data.filter(isCountryVisible).length
    : data.length;

  const toggleContinent = (continent: Continent) => {
    setSelectedContinents((prev) => {
      const next = new Set(prev);
      if (next.has(continent)) next.delete(continent);
      else next.add(continent);
      return next;
    });
  };

  const clearFilters = () => setSelectedContinents(new Set());

  // When the map is an answer surface (the quiz's "find X on the map"
  // questions), anything that names a country hands over the answer: you
  // could sweep the map until the invite line matched the country you were
  // asked for, or read the names straight off the accessibility tree.
  const isAnswerSurface = !!onCountrySelect;
  const invitesHover = showHoverInvite && !isAnswerSurface;

  const handleMouseOver = (country: Country) => {
    if (!language || !invitesHover) return;
    setHoveredCountry(country);
  };

  const handleMouseLeave = () => setHoveredCountry(undefined);

  const handleCountryClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    country: Country
  ) => {
    if (!language || wasDraggedRef.current) {
      e.preventDefault();
    } else if (onCountrySelect) {
      // Answer-picking mode: the map is the answer surface, not navigation.
      e.preventDefault();
      onCountrySelect(country);
    }
    wasDraggedRef.current = false;
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (zoom <= MIN_ZOOM) return;
    const viewport = mapViewportRef.current;
    if (!viewport) return;

    dragStateRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop,
      moved: false,
    };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    const viewport = mapViewportRef.current;
    if (!drag || !viewport) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) {
      // Only claim the pointer once a real drag starts — capturing it on
      // every press (even a plain click with no movement) redirects the
      // browser's synthetic click away from the country link underneath,
      // silently breaking normal navigation while zoomed in.
      drag.moved = true;
      viewport.setPointerCapture(drag.pointerId);
      setIsDragging(true);
    }
    if (!drag.moved) return;
    viewport.scrollLeft = drag.scrollLeft - dx;
    viewport.scrollTop = drag.scrollTop - dy;
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    if (!drag) return;

    wasDraggedRef.current = drag.moved;
    dragStateRef.current = null;
    setIsDragging(false);
    if (mapViewportRef.current?.hasPointerCapture(e.pointerId)) {
      mapViewportRef.current.releasePointerCapture(e.pointerId);
    }
  };

  const handleZoom = (next: number) => {
    setZoom(next);
    if (next <= MIN_ZOOM) mapViewportRef.current?.scrollTo(0, 0);
  };

  const announcedLine = !invitesHover
    ? ""
    : hoveredCountry
      ? inviteToExplore(hoveredCountry, language)
      : (focusedInviteToExploreLine ?? "");

  return (
    <MapContainer>
      <p role="status" aria-live="polite">
        {announcedLine}
      </p>

      {language && showControls && (
        <FilterPanel>
          <ContinentDropdown ref={continentDropdownRef}>
            <DropdownToggle
              type="button"
              $active={selectedContinents.size > 0}
              $open={isContinentMenuOpen}
              aria-haspopup="listbox"
              aria-expanded={isContinentMenuOpen}
              aria-label={t.filterLegend}
              onClick={() => setIsContinentMenuOpen((open) => !open)}
            >
              {t.continentToggle(selectedContinents.size)}
              <Caret aria-hidden="true" $open={isContinentMenuOpen}>
                ▾
              </Caret>
            </DropdownToggle>

            {isContinentMenuOpen && (
              <DropdownPanel
                role="listbox"
                aria-label={t.filterLegend}
                aria-multiselectable="true"
              >
                {FILTERABLE_CONTINENTS.map((continent) => {
                  const active = selectedContinents.has(continent);
                  return (
                    <DropdownOption
                      key={continent}
                      type="button"
                      role="option"
                      $active={active}
                      aria-selected={active}
                      onClick={() => toggleContinent(continent)}
                    >
                      <OptionCheck aria-hidden="true" $active={active}>
                        {active ? "✓" : ""}
                      </OptionCheck>
                      {continentLabels[continent]}
                    </DropdownOption>
                  );
                })}

                {hasActiveFilters && (
                  <DropdownFooter>
                    <ClearButton type="button" onClick={clearFilters}>
                      {t.clearFilters}
                    </ClearButton>
                  </DropdownFooter>
                )}
              </DropdownPanel>
            )}
          </ContinentDropdown>

          <span role="status" aria-live="polite">
            <VisuallyHidden>
              {hasActiveFilters
                ? visibleCount === 0
                  ? t.noMatches
                  : `${visibleCount}`
                : ""}
            </VisuallyHidden>
          </span>
        </FilterPanel>
      )}

      <MapViewport
        ref={mapViewportRef}
        $pannable={zoom > MIN_ZOOM}
        $dragging={isDragging}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <CustomSVG
          ref={svgRef}
          viewBox={viewBox}
          xmlns="http://www.w3.org/2000/svg"
          role="group"
          aria-label={t.mapLabel}
          $zoom={zoom}
          $fill={fillHeight}
        >
          {data.map((country, index) => {
            const isFocused = countryMatchesInput(country, focusedCountryValue);
            const isVisible = isCountryVisible(country);
            const isInteractive = !!language && isVisible;
            const displayName = isSpanish
              ? country["spanish-name"]
              : country["english-name"];
            const englishName = country["english-name"];
            const highlightFill =
              highlights?.correct === englishName
                ? "#4ade80"
                : highlights?.incorrect === englishName
                  ? "#f87171"
                  : countryColors?.[englishName];

            return (
              <MapLink
                onClick={(e) => handleCountryClick(e, country)}
                /* On an answer surface the click is intercepted anyway, and a
                   real href would print the country's name in the browser's
                   status bar on hover — another way to read off the answer. */
                to={
                  isAnswerSurface
                    ? "#"
                    : (countryTo?.(displayName) ??
                      `/countries/${displayName}`)
                }
                key={index}
                onMouseOver={() => handleMouseOver(country)}
                onMouseLeave={handleMouseLeave}
                onFocus={() => handleMouseOver(country)}
                onBlur={handleMouseLeave}
                /* Masked on an answer surface: the accessibility tree would
                   otherwise spell out the answer the question is asking for. */
                aria-label={isAnswerSurface ? t.maskedCountry : displayName}
                aria-hidden={!isVisible}
                tabIndex={isInteractive ? 0 : -1}
                data-continent={continentByCountry.get(country["english-name"])}
                $dimmed={!isVisible}
              >
                {[country.d, country["second-path"], country["third-path"]]
                  .filter(
                    (d): d is string => typeof d === "string" && d.length > 0
                  )
                  .map((d, pathIndex) => (
                    <Path
                      key={pathIndex}
                      stroke={
                        isFocused && !highlightFill ? "#fafafa" : "#182848"
                      }
                      fill={
                        highlightFill ??
                        (isFocused ? "lightblue" : "#2980b9")
                      }
                      $choseLanguage={!!language}
                      d={d}
                    />
                  ))}
              </MapLink>
            );
          })}
        </CustomSVG>
      </MapViewport>

      {language && showControls && (
        <ControlsColumn>
          <ControlButton
            type="button"
            onClick={() => handleZoom(Math.min(MAX_ZOOM, zoom + ZOOM_STEP))}
            disabled={zoom >= MAX_ZOOM}
            aria-label={t.zoomIn}
          >
            +
          </ControlButton>
          <ControlButton
            type="button"
            onClick={() => handleZoom(Math.max(MIN_ZOOM, zoom - ZOOM_STEP))}
            disabled={zoom <= MIN_ZOOM}
            aria-label={t.zoomOut}
          >
            −
          </ControlButton>
          <ControlButton
            type="button"
            onClick={() => handleZoom(MIN_ZOOM)}
            disabled={zoom === MIN_ZOOM}
            aria-label={t.zoomReset}
          >
            ⟲
          </ControlButton>
        </ControlsColumn>
      )}
    </MapContainer>
  );
};
